/**
 * Inline warning banner for meeting scheduling conflicts.
 *
 * Shows overlapping meetings (red) and adjacent meetings (amber).
 * Placed below date/time inputs in meeting creation flows.
 *
 * @module components/common/ScheduleConflictBanner
 */

import { AlertCircle, AlertTriangle } from 'lucide-react'
import { format, parseISO, isValid } from 'date-fns'

function formatTime(isoStr) {
  if (!isoStr) return ''
  const d = parseISO(isoStr)
  if (!isValid(d)) return ''
  return format(d, 'EEE, MMM d • h:mm a')
}

function MeetingLine({ meeting }) {
  const start = meeting.scheduledStartTime || meeting.startTime
  const end = meeting.scheduledEndTime || meeting.endTime
  return (
    <li className="flex items-start gap-1.5">
      <span className="shrink-0 mt-0.5">•</span>
      <span>
        <span className="font-medium">{meeting.title}</span>
        {start && (
          <span className="text-xs opacity-75 ml-1">
            {formatTime(start)}{end ? ` – ${formatTime(end)}` : ''}
          </span>
        )}
      </span>
    </li>
  )
}

/**
 * @param {{
 *   overlapping: Array,
 *   meetingBefore: object|null,
 *   meetingAfter: object|null,
 *   loading: boolean
 * }} props
 */
export default function ScheduleConflictBanner({ overlapping = [], meetingBefore, meetingAfter, loading }) {
  if (loading) return null

  const hasOverlap = overlapping.length > 0
  const hasAdjacent = meetingBefore || meetingAfter

  if (!hasOverlap && !hasAdjacent) return null

  return (
    <div className="space-y-2 mt-3">
      {hasOverlap && (
        <div className="flex items-start gap-2.5 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">
              This time overlaps with {overlapping.length} existing meeting{overlapping.length > 1 ? 's' : ''}
            </p>
            <ul className="mt-1 space-y-0.5">
              {overlapping.slice(0, 5).map((m, i) => (
                <MeetingLine key={m.meetingId || i} meeting={m} />
              ))}
              {overlapping.length > 5 && (
                <li className="text-xs opacity-75">...and {overlapping.length - 5} more</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {hasAdjacent && !hasOverlap && (
        <div className="flex items-center gap-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm">
          <AlertTriangle className="h-8 w-8 shrink-0 text-amber-600" aria-hidden />
          <div className="min-w-0 flex-1 space-y-1 text-center">
            <p className="font-medium text-amber-900">Back-to-back with another meeting</p>
            {meetingBefore && (
              <p className="text-primary-600">
                <span className="font-semibold">{meetingBefore.title}</span>
                {' '}
                ends {formatTime(meetingBefore.scheduledEndTime || meetingBefore.endTime)}
              </p>
            )}
            {meetingAfter && (
              <p className="text-primary-600">
                <span className="font-semibold">{meetingAfter.title}</span>
                {' '}
                starts {formatTime(meetingAfter.scheduledStartTime || meetingAfter.startTime)}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
